import { Component, OnInit } from "@angular/core";
import { UserService } from "../services/user.service";
import { OfframpService } from "../services/offramp.service";
import { Ng4LoadingSpinnerService } from "ng4-loading-spinner";
import { MatDatepickerInputEvent } from "@angular/material";
import * as moment from "moment";
import { NotificationsService } from "angular2-notifications";

const defaults = {
  notif: {
    timeOut: 3000,
    showProgressBar: true,
    pauseOnHover: false,
    clickToClose: true,
    maxLength: 50,
  },
};

@Component({
  selector: "app-offramp-daily-report",
  templateUrl: "./offramp-daily-report.component.html",
  styleUrls: ["./offramp-daily-report.component.css"],
})
export class OfframpDailyReportComponent implements OnInit {
  _csrf: string;

  selectedDate: Date = new Date();
  summary: any = null;
  summaryLoaded: boolean = false;

  capitalAllocatedUSDT: number = 0;
  capitalAllocatedINR: number = 0;
  latestCapitalAllocationRate: number = 0;

  offrampMonitorRow: any = null;

  dashboard = {
    totalTradesMTD: 0,
    totalBuyVolumeINR: 0,
    totalReceivedINR: 0,
    grossPnlMTD: 0,
    totalProfitShareMTD: 0,
    finalNetPnlMTD: 0,
    totalExpensesMTD: 0,
    netPnlAfterExpensesMTD: 0,
  };
  dashboardLoaded: boolean = false;

  myFilter = (d: Date | null): boolean => {
    return true;
  };

  constructor(
    private notifSer: NotificationsService,
    private userService: UserService,
    private offrampService: OfframpService,
    private spinnerService: Ng4LoadingSpinnerService
  ) { }

  ngOnInit() {
    this.csrf();
  }

  csrf() {
    this.userService.csrf().subscribe(
      (success) => {
        this._csrf = success._csrf;
        this.loadCapitalAllocationTotals();
        this.loadDailySummary();
        this.loadDashboardSummary();
        this.loadOfframpMonitorSummary();
      },
      (error) => {
        this.notifSer.error("Error", error.error.message, defaults.notif);
      }
    );
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.selectedDate = event.value;
      this.loadDailySummary();
      this.loadCapitalAllocationTotals();
      this.loadOfframpMonitorSummary();
    }
  }

  get selectedDateStr(): string {
    return moment(this.selectedDate).format("YYYY-MM-DD");
  }

  get monthStartStr(): string {
    return moment().startOf("month").format("YYYY-MM-DD");
  }

  get todayStr(): string {
    return moment().format("YYYY-MM-DD");
  }

  loadDailySummary() {
    this.spinnerService.show();
    const filter = {
      fromDate: this.selectedDateStr,
      toDate: this.selectedDateStr,
      page: 1,
      limit: 1,
      _csrf: this._csrf
    };

    this.offrampService.getAllOfframpDailySummary(filter).subscribe(
      (response: any) => {
        const list = (response && response.data) || [];
        this.summary = list.length ? list[0] : null;
        this.summaryLoaded = true;
        this.spinnerService.hide();
      },
      (error) => {
        this.spinnerService.hide();
        this.summary = null;
        this.summaryLoaded = true;
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch daily summary", defaults.notif);
      }
    );
  }

  loadCapitalAllocationTotals() {
    const filter = { page: 1, limit: 10000, _csrf: this._csrf };

    this.offrampService.getAllOfframpCapitalAllocations(filter).subscribe(
      (response: any) => {
        const list = (response && response.data) || [];
        this.capitalAllocatedUSDT = list.reduce((sum: number, item: any) => sum + (item.capital_allocated || 0), 0);
        this.capitalAllocatedINR = list.reduce((sum: number, item: any) => sum + (item.capital_allocated_convert || 0), 0);

        const latest = list.reduce((latestItem: any, item: any) => {
          if (!latestItem) return item;
          return new Date(item.createdAt) > new Date(latestItem.createdAt) ? item : latestItem;
        }, null);
        this.latestCapitalAllocationRate = latest ? latest.rate || 0 : 0;
      },
      (error) => {
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch capital allocation totals", defaults.notif);
      }
    );
  }

  loadOfframpMonitorSummary() {
    const filter = {
      fromDate: this.selectedDateStr,
      toDate: this.selectedDateStr,
      userId: "",
      _csrf: this._csrf
    };

    this.offrampService.getOfframpMonitorDailyReport(filter).subscribe(
      (response: any) => {
        const list = Array.isArray(response) ? response : [];
        this.offrampMonitorRow = list.length ? list[0] : null;
      },
      (error) => {
        this.offrampMonitorRow = null;
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch offramp monitor summary", defaults.notif);
      }
    );
  }

  get inrPaidOutViaOfframp(): number {
    return this.offrampMonitorRow ? (this.offrampMonitorRow.totalPayoutIn || 0) : 0;
  }

  get usdtBoughtOnCredit(): number {
    return this.offrampMonitorRow ? (this.offrampMonitorRow.creditsAdded || 0) : 0;
  }

  get repaidByClientToday(): number {
    return this.offrampMonitorRow ? (this.offrampMonitorRow.runningBalance || 0) : 0;
  }

  get pendingOutstandingBalanceUSDT(): number {
    return this.offrampMonitorRow ? (this.offrampMonitorRow.totalPayoutIn || 0) : 0;
  }

  get pendingOutstandingBalanceINR(): number {
    return this.pendingOutstandingBalanceUSDT * this.latestCapitalAllocationRate;
  }

  loadDashboardSummary() {
    const summaryFilter = {
      fromDate: this.monthStartStr,
      toDate: this.todayStr,
      page: 1,
      limit: 31,
      _csrf: this._csrf
    };

    this.offrampService.getAllOfframpDailySummary(summaryFilter).subscribe(
      (response: any) => {
        const list = (response && response.data) || [];
        this.dashboard.totalProfitShareMTD = list.reduce((sum: number, item: any) => sum + (item.totalProfitShareINR || 0), 0);
        this.dashboard.finalNetPnlMTD = list.reduce((sum: number, item: any) => sum + (item.finalNetPnlINR || 0), 0);
        this.dashboard.totalExpensesMTD = list.reduce((sum: number, item: any) => sum + (item.dailyExpensesINR || 0), 0);
        this.dashboard.netPnlAfterExpensesMTD = list.reduce((sum: number, item: any) => sum + (item.netPnlAfterExpensesINR || 0), 0);
        this.dashboardLoaded = true;
      },
      (error) => {
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch dashboard summary", defaults.notif);
      }
    );

    // Trades logged, buy volume, received and gross P&L come from
    // offramp_dailylog (one row per matched trade slice) rather than the
    // pre-aggregated offramp_daily_summary, so they reflect the trades
    // actually logged for the month.
    const dailyLogFilter = {
      fromDate: this.monthStartStr,
      toDate: this.todayStr,
      page: 1,
      limit: 10000,
      _csrf: this._csrf
    };

    this.offrampService.getAllOfframpDailyLog(dailyLogFilter).subscribe(
      (response: any) => {
        const list = (response && response.data) || [];
        this.dashboard.totalTradesMTD = (response && response.total) || list.length;
        this.dashboard.totalBuyVolumeINR = list.reduce((sum: number, item: any) => sum + (item.BuyTotal || 0), 0);
        this.dashboard.totalReceivedINR = list.reduce((sum: number, item: any) => sum + (item.SellTotal || 0), 0);
        this.dashboard.grossPnlMTD = list.reduce((sum: number, item: any) => sum + (item.GrossPnL || 0), 0);
      },
      (error) => {
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to fetch daily log totals", defaults.notif);
      }
    );
  }

  regenerate() {
    this.spinnerService.show();
    this.offrampService.regenerateOfframpDailySummary({ date: this.selectedDateStr, _csrf: this._csrf }).subscribe(
      (response: any) => {
        this.spinnerService.hide();
        if (response.status || response.success) {
          this.notifSer.success("Success", response.message || "Daily summary regenerated", defaults.notif);
          this.loadDailySummary();
          this.loadCapitalAllocationTotals();
          this.loadDashboardSummary();
          this.loadOfframpMonitorSummary();
        } else {
          this.notifSer.error("Error", response.message || "Failed to regenerate daily summary", defaults.notif);
        }
      },
      (error) => {
        this.spinnerService.hide();
        this.notifSer.error("Error", (error.error && error.error.message) || "Failed to regenerate daily summary", defaults.notif);
      }
    );
  }

  formatINR(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatUSDT(value: any): string {
    if (value === null || value === undefined || isNaN(value)) return '-';
    return Number(value).toLocaleString('en-IN', { minimumFractionDigits: 8, maximumFractionDigits: 8 });
  }

  get actualClosingBalanceDisplay(): string {
    if (!this.summary || this.summary.actualClosingBalanceINR === null || this.summary.actualClosingBalanceINR === undefined) {
      return 'Not entered';
    }
    return this.formatINR(this.summary.actualClosingBalanceINR);
  }
}



// WEBPACK FOOTER //
// ./src/app/offramp-daily-report/offramp-daily-report.component.ts